import { supabase } from "@/lib/supabase";

// Simple encryption utility (for production, use a proper encryption library)
const encryptOTP = (otp, email) => {
  // Simple XOR encryption with email as key (for demo purposes)
  // In production, use a proper encryption library like crypto-js
  const key = email.slice(0, 8);
  let encrypted = '';
  for (let i = 0; i < otp.length; i++) {
    const charCode = otp.charCodeAt(i) ^ key.charCodeAt(i % key.length);
    encrypted += String.fromCharCode(charCode);
  }
  return btoa(encrypted); // Base64 encode
};

const decryptOTP = (encrypted, email) => {
  try {
    const decoded = atob(encrypted);
    const key = email.slice(0, 8);
    let decrypted = '';
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length);
      decrypted += String.fromCharCode(charCode);
    }
    return decrypted;
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

export const emailService = {
  // Generate random OTP
  generateOTP: () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  },

  // Store encrypted OTP
  storeOTP: (email, otp) => {
    const expiresAt = Date.now() + (10 * 60 * 1000); // 10 minutes

    // Encrypt the OTP
    const encryptedOTP = encryptOTP(otp, email);

    const otpData = {
      email,
      encryptedOTP, // Store encrypted OTP
      expiresAt,
      verified: false,
      attempts: 0,
      createdAt: Date.now()
    };

    // Store in both localStorage and sessionStorage
    const storageData = JSON.stringify(otpData);
    localStorage.setItem(`otp_${email}`, storageData);
    sessionStorage.setItem(`otp_${email}`, storageData);

    return true;
  },

  // Get decrypted OTP data
  getOTPData: (email) => {
    const stored = localStorage.getItem(`otp_${email}`);
    if (!stored) return null;

    try {
      const data = JSON.parse(stored);
      // Decrypt the OTP when needed
      if (data.encryptedOTP) {
        data.otp = decryptOTP(data.encryptedOTP, email);
        delete data.encryptedOTP; // Remove encrypted version from memory
      }
      return data;
    } catch (error) {
      console.error("Error parsing OTP:", error);
      return null;
    }
  },

  // Verify OTP
  verifyOTP: (email, otp) => {
    const otpData = emailService.getOTPData(email);

    if (!otpData) {
      return { success: false, error: "No verification code found. Please request a new one." };
    }

    // Expiry check
    if (Date.now() > otpData.expiresAt) {
      emailService.clearOTP(email);
      return { success: false, error: "Verification code has expired. Please request a new one." };
    }

    // Already used
    if (otpData.verified) {
      return { success: false, error: "This code has already been used." };
    }

    // Attempt limit (max 5 tries)
    if (otpData.attempts >= 5) {
      emailService.clearOTP(email);
      return { success: false, error: "Too many attempts. Please request a new OTP." };
    }

    // Increase attempts and update storage
    otpData.attempts += 1;
    
    // Re-encrypt and store updated data
    const updatedData = {
      email,
      encryptedOTP: encryptOTP(otpData.otp, email),
      expiresAt: otpData.expiresAt,
      verified: otpData.verified,
      attempts: otpData.attempts,
      createdAt: otpData.createdAt
    };
    localStorage.setItem(`otp_${email}`, JSON.stringify(updatedData));
    sessionStorage.setItem(`otp_${email}`, JSON.stringify(updatedData));

    // Incorrect OTP
    if (otpData.otp !== otp) {
      return { success: false, error: "Invalid verification code. Please try again." };
    }

    // Success
    // Clear after successful verification for security
    emailService.clearOTP(email);

    return { success: true };
  },

  // Clear OTP
  clearOTP: (email) => {
    localStorage.removeItem(`otp_${email}`);
    sessionStorage.removeItem(`otp_${email}`);
  },

  // Send OTP email
  sendOTP: async (email, name = "") => {
    const otp = emailService.generateOTP();

    // Send email FIRST
    const { error } = await supabase.functions.invoke("resend-email", {
      body: {
        to: email,
        subject: `Your Verification Code: ${otp}`,
        html: `
          <!DOCTYPE html>
            <html lang="en">
            <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width,initial-scale=1.0">
            <meta http-equiv="X-UA-Compatible" content="IE=edge">
            </head>
            <body style="margin:0;padding:0;background-color:#0D0D0F;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;">
            <div style="display:none;max-height:0;overflow:hidden;mso-hide:all;">Enter this code to verify your email.&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;</div>
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#0D0D0F;">
            <tr><td align="center" style="padding:40px 16px 60px 16px;">
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width:600px;width:100%;">
                <!-- LOGO -->
                <tr><td style="padding-bottom:32px;" align="left">
                <img 
                    src="https://xlowyobpjgwcdaccyqwn.supabase.co/storage/v1/object/public/assets/logo-full-dark.svg"
                    width="160"
                    alt="DecX AI"
                    style="display:block; max-width:160px; height:auto;"
                    />
                </td></tr><tr><td>
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%"
                style="background-color:#111318;border-radius:16px;border:1px solid #1E2330;overflow:hidden;"><tr><td style="background:linear-gradient(90deg,#004AAD 0%,#0082FB 100%);height:3px;font-size:0;line-height:0;">&nbsp;</td></tr><tr><td style="padding:48px 48px 40px 48px;"><table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin-bottom:24px;">
            <tr><td style="width:52px;height:52px;background:linear-gradient(135deg,#004AAD,#0082FB);border-radius:12px;text-align:center;vertical-align:middle;">
                <span style="font-size:24px;line-height:52px;">&#128273;</span>
            </td></tr>
            </table><h1 style="margin:0 0 12px 0;font-family:'Inter',-apple-system,sans-serif;font-size:30px;font-weight:700;line-height:1.2;color:#FFFFFF;letter-spacing:-0.5px;">Your verification code</h1><p style="margin:0 0 36px 0;font-family:'Inter',-apple-system,sans-serif;font-size:16px;color:#8892A4;line-height:1.65;">Enter this code to verify your email and complete your signup.</p><table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-bottom:28px;">
            <tr><td align="center" style="background-color:#0A1628;border:2px solid #0082FB;border-radius:14px;padding:36px 40px;">
                <p style="margin:0 0 10px 0;font-family:'Inter',-apple-system,sans-serif;font-size:11px;font-weight:600;color:#0082FB;letter-spacing:2px;text-transform:uppercase;">Verification Code</p>
                <p style="margin:0;font-family:'Inter',-apple-system,sans-serif;font-size:48px;font-weight:800;color:#FFFFFF;letter-spacing:12px;line-height:1;">${otp}</p>
            </td></tr>
            </table>
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-bottom:28px;">
            <tr><td align="center">
                <p style="margin:0;font-family:'Inter',-apple-system,sans-serif;font-size:13px;color:#8892A4;">&#9200;&nbsp; This code expires in <strong style="color:#D97706;">1 Hour</strong></p>
            </td></tr>
            </table>
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#0D1117;border:1px solid #1A2035;border-radius:10px;">
            <tr><td style="padding:14px 20px;">
                <p style="margin:0;font-family:'Inter',-apple-system,sans-serif;font-size:13px;color:#5A6478;line-height:1.5;">Didn&rsquo;t request this? Ignore this email &mdash; your account is safe.</p>
            </td></tr>
            </table></td></tr></table></td></tr><tr><td style="padding:32px 8px 0 8px;">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr><td style="border-top:1px solid #1E2330;padding-top:28px;">
                <p style="margin:0 0 4px 0;font-family:'Inter',-apple-system,sans-serif;font-size:14px;color:#8892A4;">&mdash; Team DecX AI</p>
                <p style="margin:8px 0 0 0;font-family:'Inter',-apple-system,sans-serif;font-size:13px;font-weight:500;color:#0082FB;">decx.ai</p>
                </td></tr>
                
            </table>
            </td></tr>
                <!-- FOOTER -->
                <tr><td style="padding-top:40px;">
                    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                    <tr><td align="center" style="padding:20px 24px;background-color:#0A0B0E;border-radius:12px;border:1px solid #1A1D24;">
                        <p style="margin:0 0 6px 0;font-family:'Inter',-apple-system,sans-serif;font-size:12px;color:#3A4152;">
                        You&rsquo;re receiving this from <strong style="color:#4A5568;">DecX AI</strong> &middot;
                        <a href="#" style="color:#3A4152;text-decoration:underline;">Unsubscribe</a> &middot;
                        <a href="#" style="color:#3A4152;text-decoration:underline;">View in browser</a>
                        </p>
                        <p style="margin:0;font-family:'Inter',-apple-system,sans-serif;font-size:11px;color:#2A3040;">&copy; 2025 DecX AI. All rights reserved.</p>
                    </td></tr>
                    </table>
                </td></tr>
                </table>
            </td></tr>
            </table>
            </body>
            </html>
        `
      }
    });

    if (error) {
      console.error("Error sending email:", error);
      return { success: false, error: "Failed to send verification email" };
    }

    // Store encrypted OTP only after email success
    emailService.storeOTP(email, otp);

    return { success: true };
  },

  // Check if OTP exists and is valid
  hasValidOTP: (email) => {
    const otpData = emailService.getOTPData(email);
    if (!otpData) return false;
    if (otpData.verified) return false;
    if (Date.now() > otpData.expiresAt) return false;
    if (otpData.attempts >= 5) return false;
    return true;
  },

  // Get remaining time in seconds
  getRemainingTime: (email) => {
    const otpData = emailService.getOTPData(email);
    if (!otpData) return 0;
    const remaining = Math.max(0, Math.floor((otpData.expiresAt - Date.now()) / 1000));
    return remaining;
  }
};